import { useEffect, useRef, useCallback } from "react";
import { isMobile, isIOS, isSafari, isChrome, isMobileSafari } from "react-device-detect";
import { nowInKyiv } from "@/src/shared/utils/dateTime";
import { useAnalytics } from "./useAnalytics";
import { AnalyticsAction } from "@/src/shared/config/analytics-config";
import { logAnalyticsAction } from "@/src/api/server-actions/analytics";

export const useDiagnosticAnalytics = (activePage: string, stepAnalytics: any) => {
    const { trackAnalyticEvent } = useAnalytics();
    const hasLogMount = useRef(false);
    const sentEvents = useRef<Set<string>>(new Set());

    const getTechnicalContext = useCallback(() => {
        return {
            ua: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
            platform: typeof navigator !== "undefined" ? (navigator as any).platform : "unknown",
            isMobile,
            isIOS,
            isSafari,
            isChrome,
            isMobileSafari,
            url: typeof window !== "undefined" ? window.location.href : "",
            path: typeof window !== "undefined" ? window.location.pathname : "",
            referrer: typeof document !== "undefined" ? document.referrer : "",
            screen: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "",
            timestamp: nowInKyiv().toISO(),
            visibility: typeof document !== "undefined" ? document.visibilityState : "unknown",
        };
    }, []);

    const logDiagnostic = useCallback((message: string, data?: any) => {
        const context = getTechnicalContext();
        try {
            logAnalyticsAction({
                message: `[DIAGNOSTIC][${context.timestamp}] ${message}`,
                ...data,
                context
            });
        } catch (e) {
        }
    }, [getTechnicalContext]);

    useEffect(() => {
        if (!hasLogMount.current) {
            logDiagnostic("StepPage Mounted", { activePage });
            hasLogMount.current = true;
        }

        const handleVisibilityChange = () => {
            logDiagnostic("Visibility Changed", { 
                visibilityState: document.visibilityState,
                activePage 
            });
        };

        const handlePageHide = () => {
            logDiagnostic("Page Hide", { activePage });
        };

        window.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pagehide", handlePageHide);

        return () => {
            logDiagnostic("StepPage Unmounted", { activePage });
            window.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("pagehide", handlePageHide);
        };
    }, [activePage, logDiagnostic]);

    useEffect(() => {
        if (!stepAnalytics) return;

        const eventName = stepAnalytics.eventName || stepAnalytics.eventStaticName;
        
        if (eventName === "thank_you_loaded" || (typeof eventName === 'string' && eventName.includes("thank_you"))) {
            if (sentEvents.current.has(eventName)) return;
            
            logDiagnostic("Thank You Event Detected", { 
                eventName, 
                stepAnalytics,
                activePage 
            });

            try {
                logDiagnostic("Attempting to send Thank You Event", { eventName });
                
                trackAnalyticEvent(eventName as AnalyticsAction);
                
                sentEvents.current.add(eventName);
                logDiagnostic("Thank You Event Send Called (Check GTM/Network)", { eventName });
            } catch (error) {
                logDiagnostic("Error sending Thank You Event", { 
                    eventName, 
                    error: error instanceof Error ? error.message : String(error) 
                });
            }
        }
    }, [stepAnalytics, activePage, logDiagnostic, trackAnalyticEvent]);

    return { logDiagnostic, getTechnicalContext };
};
